import React, { useState, useRef, useEffect } from "react";
import { Lock } from "lucide-react";
import { uploadEmployeeDocument } from "../../services/documentService";
import { apiFetch, useAuth } from "../../../../context/AuthContext";
import { useWorkingCompanyOptions } from "../../hooks/useWorkingCompanyOptions";
import { DOCUMENT_CHECKLIST_FIELDS } from "../../constants/documentChecklistFields";
import ReusableDropdown from "../../../../components/ReusableDropdown";
import DatePicker from "../../../../components/DatePicker";
import { parseEgyptianNationalId, normalizeNationalId } from "../../../../utils/egyptianNationalId";
import {
  Edit,
  X,
  User,
  Building,
  CheckCircle,
  DollarSign,
  Award,
  Home,
  Heart,
  Flag,
  Save,
  Upload,
  FileText,
  Trash2,
} from "../../constants/icons";

const API_URL = process.env.REACT_APP_BASE_URL;

// ─── Full employee field catalog (mirrors the backend Prisma schema) ─────────
// Every editable column of the employees table, grouped into sections.
// `name` is the backend field name — formData is keyed by these names and the
// payload is sent to PUT /employees/:infinityCode as-is.
export const FIELD_SECTIONS = [
  {
    key: "basic",
    title: { en: "Basic Information", ar: "المعلومات الأساسية" },
    icon: User,
    fields: [
      { name: "sr", label: { en: "Full Name (English)", ar: "الاسم الكامل (إنجليزي)" }, type: "text", required: true },
      { name: "arabicName", label: { en: "Full Name (Arabic)", ar: "الاسم الكامل (عربي)" }, type: "text", dir: "rtl" },
      { name: "email", label: { en: "Email", ar: "البريد الإلكتروني" }, type: "email" },
      { name: "phoneNumber", label: { en: "Phone Number", ar: "رقم الهاتف" }, type: "tel" },
      { name: "emergencyPhoneNumber", label: { en: "Emergency Phone", ar: "هاتف الطوارئ" }, type: "tel" },
      { name: "nationalId", label: { en: "National ID", ar: "الرقم القومي" }, type: "text" },
      { name: "idRegion", label: { en: "ID Region", ar: "منطقة إصدار البطاقة" }, type: "text" },
      { name: "dob", label: { en: "Date of Birth", ar: "تاريخ الميلاد" }, type: "date" },
      { name: "age", label: { en: "Age", ar: "العمر" }, type: "number" },
      {
        name: "gender",
        label: { en: "Gender", ar: "النوع" },
        type: "select",
        options: [
          { key: "Male", label: { en: "Male", ar: "ذكر" } },
          { key: "Female", label: { en: "Female", ar: "أنثى" } },
        ],
      },
      {
        name: "maritalStatus",
        label: { en: "Marital Status", ar: "الحالة الاجتماعية" },
        type: "select",
        options: [
          { key: "Single", label: { en: "Single", ar: "أعزب" } },
          { key: "Married", label: { en: "Married", ar: "متزوج" } },
          { key: "Divorced", label: { en: "Divorced", ar: "مطلق" } },
          { key: "Widowed", label: { en: "Widowed", ar: "أرمل" } },
        ],
      },
      { name: "nationality", label: { en: "Nationality", ar: "الجنسية" }, type: "text" },
      { name: "religion", label: { en: "Religion", ar: "الديانة" }, type: "text" },
      { name: "militaryStatus", label: { en: "Military Status", ar: "الموقف من التجنيد" }, type: "text" },
    ],
  },
  {
    key: "address",
    title: { en: "Address", ar: "العنوان" },
    icon: Home,
    fields: [
      { name: "address", label: { en: "Address (English)", ar: "العنوان (إنجليزي)" }, type: "text" },
      { name: "arabicAddress", label: { en: "Address (Arabic)", ar: "العنوان (عربي)" }, type: "text", dir: "rtl" },
      { name: "city", label: { en: "City", ar: "المدينة" }, type: "text" },
      { name: "country", label: { en: "Country", ar: "الدولة" }, type: "text" },
    ],
  },
  {
    key: "work",
    title: { en: "Work Information", ar: "معلومات العمل" },
    icon: Building,
    fields: [
      { name: "workingCompany", label: { en: "Working Company", ar: "الشركة العاملة" }, type: "workingCompany" },
      { name: "departmentId", label: { en: "Department", ar: "القسم" }, type: "department", required: true },
      { name: "position", label: { en: "Position", ar: "المسمى الوظيفي" }, type: "text", required: false },
      { name: "location", label: { en: "Location", ar: "الموقع" }, type: "text" },
      // clientName is intentionally READ-ONLY here — it must be immutable
      // outside the dedicated superadmin "rename client" operation, which
      // renames it for every employee under that client at once rather than
      // letting it drift per-record through this form. See
      // EmployeeManagement.jsx's handleUpdateEmployee (never sends it) and
      // employeeService.coerceEmployeeUpdateData (backend strips it too).
      { name: "clientName", label: { en: "Client Name", ar: "اسم العميل" }, type: "text", readOnly: true },
      // Set once on Add Employee; never editable (the backend ignores it on update too).
      {
        name: "clientCode",
        label: { en: "Client Code", ar: "كود العميل" },
        type: "text",
        readOnly: true,
        readOnlyHint: {
          en: "Not editable — the Client Code is fixed once the employee is added",
          ar: "غير قابل للتعديل — كود العميل ثابت بعد إضافة الموظف",
        },
      },
      { name: "contractCompany", label: { en: "Contract Company", ar: "شركة التعاقد" }, type: "text" },
      {
        name: "employeeState",
        label: { en: "Status", ar: "الحالة" },
        type: "select",
        options: [
          { key: "Active", label: { en: "Active", ar: "نشط" } },
          { key: "OnLeave", label: { en: "On Leave", ar: "في إجازة" } },
          { key: "Inactive", label: { en: "Inactive", ar: "غير نشط" } },
        ],
      },
      {
        name: "laborMode",
        label: { en: "Labor Mode", ar: "نمط العمل" },
        type: "select",
        options: [
          { key: "Insource", label: { en: "Insource", ar: "داخلي (توظيف مباشر)" } },
          { key: "Outsource", label: { en: "Outsource", ar: "خارجي (تعاقد)" } },
        ],
      },
      { name: "hiringDate", label: { en: "Hiring Date", ar: "تاريخ التعيين" }, type: "date" },
      { name: "resignedDate", label: { en: "Resigned Date", ar: "تاريخ الاستقالة" }, type: "date" },
      { name: "resignReasons", label: { en: "Resign Reasons", ar: "أسباب الاستقالة" }, type: "text" },
      { name: "workingAgeYears", label: { en: "Working Age (Years)", ar: "سنوات الخبرة" }, type: "number" },
      { name: "workingAgeMonths", label: { en: "Working Age (Months)", ar: "شهور الخبرة" }, type: "number" },
    ],
  },
  {
    key: "management",
    title: { en: "Management", ar: "الإدارة" },
    icon: Award,
    fields: [
      { name: "firstLineManagerClientCode", label: { en: "1st Line Manager", ar: "المدير المباشر" }, type: "managerDropdown" },
    ],
  },
  {
    key: "compensation",
    title: { en: "Compensation", ar: "الرواتب والبدلات" },
    icon: DollarSign,
    fields: [
      { name: "grossSalary", label: { en: "Gross Salary", ar: "الراتب الإجمالي" }, type: "number" },
      { name: "netSalary", label: { en: "Net Salary", ar: "الراتب الصافي" }, type: "number" },
      { name: "variableSalary", label: { en: "Variable Salary", ar: "الراتب المتغير" }, type: "number" },
      { name: "transportationAllowances", label: { en: "Transportation Allowances", ar: "بدل المواصلات" }, type: "number" },
      { name: "insuranceSalary", label: { en: "Insurance Salary", ar: "الراتب التأميني" }, type: "number" },
      { name: "accountNumber", label: { en: "Account Number", ar: "رقم الحساب" }, type: "text" },
      { name: "bankName", label: { en: "Bank Name", ar: "اسم البنك" }, type: "text" },
    ],
  },
  {
    key: "contract",
    title: { en: "Contract & Regularization", ar: "التعاقد والتثبيت" },
    icon: FileText,
    fields: [
      { name: "contractDate", label: { en: "Contract Date", ar: "تاريخ العقد" }, type: "date" },
      { name: "termOfContract", label: { en: "Term of Contract (months)", ar: "مدة العقد (شهور)" }, type: "number" },
      { name: "contractExpiration", label: { en: "Contract Expiration", ar: "انتهاء العقد" }, type: "date" },
      { name: "lastContractDate", label: { en: "Last Contract Date", ar: "تاريخ آخر عقد" }, type: "date" },
      {
        name: "lastContractTerm",
        label: { en: "Last Contract Term", ar: "مدة آخر عقد" },
        type: "select",
        options: [
          { key: "Full-time", label: { en: "Full-time", ar: "دوام كامل" } },
          { key: "Part-time", label: { en: "Part-time", ar: "دوام جزئي" } },
          { key: "Contract", label: { en: "Contract", ar: "تعاقد" } },
          { key: "Temporary", label: { en: "Temporary", ar: "مؤقت" } },
          { key: "Freelance", label: { en: "Freelance", ar: "عمل حر" } },
        ],
      },
      { name: "lastContractExpiration", label: { en: "Last Contract Expiration", ar: "انتهاء آخر عقد" }, type: "date" },
      { name: "toBeRegular", label: { en: "To Be Regular", ar: "قابل للتثبيت" }, type: "boolean" },
      { name: "beRegularDate", label: { en: "Be-Regular Date", ar: "تاريخ التثبيت" }, type: "date" },
      { name: "periodForBeingRegular", label: { en: "Period for Being Regular", ar: "فترة التثبيت" }, type: "text" },
    ],
  },
  {
    key: "insurance",
    title: { en: "Insurance & Medical", ar: "التأمين والرعاية الطبية" },
    icon: Heart,
    fields: [
      { name: "insuranceStatus", label: { en: "Insurance Status", ar: "حالة التأمين" }, type: "text" },
      { name: "insuranceNumber", label: { en: "Insurance Number", ar: "الرقم التأميني" }, type: "text" },
      { name: "insuredBeginningDate", label: { en: "Insurance Start Date", ar: "تاريخ بداية التأمين" }, type: "date" },
      { name: "monthsForInsurance", label: { en: "Months for Insurance", ar: "شهور التأمين" }, type: "number" },
      { name: "medicalInsuranceStage", label: { en: "Medical Insurance Stage", ar: "فئة التأمين الطبي" }, type: "text" },
      { name: "medicalNumber", label: { en: "Medical Number", ar: "الرقم الطبي" }, type: "text" },
      { name: "didMedicalCheck", label: { en: "Did Medical Check", ar: "أجرى الفحص الطبي" }, type: "boolean" },
      { name: "delivered", label: { en: "Delivered", ar: "تم التسليم" }, type: "boolean" },
    ],
  },
  {
    key: "education",
    title: { en: "Education", ar: "التعليم" },
    icon: Flag,
    fields: [
      { name: "graduateInstitution", label: { en: "Graduate Institution", ar: "جهة التخرج" }, type: "text" },
      { name: "major", label: { en: "Major", ar: "التخصص" }, type: "text" },
      { name: "graduationYear", label: { en: "Graduation Year", ar: "سنة التخرج" }, type: "number" },
    ],
  },
  {
    key: "licenses",
    title: { en: "Licenses & Health Certificates", ar: "الرخص والشهادات الصحية" },
    icon: CheckCircle,
    fields: [
      { name: "drivingLicenseDegree", label: { en: "Driving License Degree", ar: "درجة رخصة القيادة" }, type: "text" },
      { name: "drivingLicenseExpiry", label: { en: "Driving License Expiry", ar: "انتهاء رخصة القيادة" }, type: "date" },
      { name: "drivingLicenseStillValid", label: { en: "Driving License Valid", ar: "رخصة القيادة سارية" }, type: "boolean" },
      { name: "healthCertificateOB", label: { en: "Health Certificate (Office Boys)", ar: "شهادة صحية (أوفيس بوي)" }, type: "boolean" },
      { name: "healthCertExpiryDate", label: { en: "Health Certificate Expiry", ar: "انتهاء الشهادة الصحية" }, type: "date" },
      { name: "healthCertStillValid", label: { en: "Health Certificate Valid", ar: "الشهادة الصحية سارية" }, type: "boolean" },
      { name: "uniformSize", label: { en: "Uniform Size", ar: "مقاس الزي" }, type: "text" },
    ],
  },
  {
    key: "documents",
    title: { en: "Documents Checklist", ar: "قائمة المستندات" },
    icon: FileText,
    fields: DOCUMENT_CHECKLIST_FIELDS,
  },
];

// The edit form leaves the Documents Checklist out: it has its own button
// (DocumentsModal). The section stays in FIELD_SECTIONS for the view modal.
const EDIT_FORM_SECTIONS = FIELD_SECTIONS.filter((section) => section.key !== "documents");
const DOCUMENT_FIELD_NAMES = new Set(DOCUMENT_CHECKLIST_FIELDS.map((field) => field.name));
const COMPENSATION_FIELD_NAMES = new Set([
  ...(FIELD_SECTIONS.find((section) => section.key === "compensation")?.fields || []).map((f) => f.name),
  "salaryInputSource",
]);

const ALL_FIELDS = FIELD_SECTIONS.flatMap((section) => section.fields);

const toDateInput = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? "" : parsed.toISOString().split("T")[0];
};

// Build the form state for the complete field set from a raw employee record.
const buildFormState = (record = {}) => {
  const state = {};
  ALL_FIELDS.forEach((field) => {
    const value = record[field.name];
    if (field.type === "date") state[field.name] = toDateInput(value);
    else if (field.type === "boolean") state[field.name] = Boolean(value);
    else state[field.name] = value ?? "";
  });
  // Always carry manager fields — firstLineManagerName is not a UI field in
  // FIELD_SECTIONS (hidden behind the dropdown) but must be included in the
  // submit payload so the backend resolver can match by name too.
  if (record.firstLineManagerName !== undefined) {
    state.firstLineManagerName = record.firstLineManagerName ?? "";
  }
  if (record.firstLineManagerClientCode !== undefined) {
    state.firstLineManagerClientCode = record.firstLineManagerClientCode ?? "";
  }
  return state;
};

const EditEmployeeModal = ({
  selectedEmployee = null,
  setShowEditEmployee = () => {},
  setSelectedEmployee = () => {},
  language = "en",
  text = {},
  unifiedControlStyle = () => ({}),
  departments = [],
  ReusableDropdown = () => null,
  handleUpdateEmployee = async () => {},
  handleDeleteEmployee = async () => {},
  theme = "light",
  screenSize = "md",
  fonts = {},
  spacing = {},
  styles: stylesProp = {},
  loading = false,
  handlePrimaryBtnMouseEnter = () => {},
  handlePrimaryBtnMouseLeave = () => {},
  clientName = "",
  companies = [],
  // Compensation section (salaries + bank). Same rule as the server, which
  // only returns those fields to real admins and to admin levels with the
  // employees 'export' feature — without it the section would only show
  // blanks, so it's hidden and never submitted.
  canViewCompensation = true,
}) => {
  const formSections = canViewCompensation
    ? EDIT_FORM_SECTIONS
    : EDIT_FORM_SECTIONS.filter((section) => section.key !== "compensation");
  const isDark = theme === "dark";
  const { hasFeatureAccess } = useAuth();
  const canAddDepartment = hasFeatureAccess("departments", "add");

  const defaultStyles = {
    modal: {
      position: "fixed",
      inset: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "rgba(0,0,0,0.5)",
      zIndex: 9999,
      backdropFilter: "blur(3px)",
      animation: "fadeIn 300ms ease-out",
    },
    modalContent: {
      position: "relative",
      width: "92%",
      maxWidth: 1000,
      maxHeight: "92vh",
      overflowY: "auto",
      background: isDark
        ? "linear-gradient(135deg, #0f172a 0%, #1a2a3a 100%)"
        : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
      color: isDark ? "#e6eef8" : "#0f172a",
      borderRadius: 16,
      padding: "32px",
      boxShadow: isDark
        ? "0 25px 60px rgba(0,0,0,0.8), 0 0 1px rgba(255,255,255,0.1)"
        : "0 20px 50px rgba(0,0,0,0.12), 0 0 1px rgba(0,0,0,0.04)",
      border: isDark
        ? "1px solid rgba(255,255,255,0.1)"
        : "1px solid rgba(148,163,184,0.15)",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      animation: "slideUp 400ms cubic-bezier(0.34, 1.56, 0.64, 1)",
    },
    modalHeader: {
      marginBottom: 32,
      paddingBottom: 16,
      borderBottom: isDark
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(0,0,0,0.08)",
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    modalTitle: {
      margin: 0,
      fontSize: 24,
      fontWeight: 800,
      background: isDark
        ? "linear-gradient(135deg, #fff 0%, #cbd5e1 100%)"
        : "linear-gradient(135deg, #0f172a 0%, #0f172a 100%)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
      letterSpacing: "-0.5px",
      display: "flex",
      alignItems: "center",
      gap: 10,
    },
    modalActions: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 12,
      marginTop: 32,
      paddingTop: 20,
      borderTop: isDark
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(0,0,0,0.08)",
    },
    primaryBtn: {
      background: "linear-gradient(135deg, #2e7cba 0%, #2e7cba 100%)",
      color: "#fff",
      padding: "12px 24px",
      borderRadius: 10,
      border: "none",
      display: "inline-flex",
      gap: 8,
      alignItems: "center",
      cursor: "pointer",
      fontWeight: 700,
      fontSize: 14,
      transition: "all 300ms cubic-bezier(0.34, 1.56, 0.64, 1)",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    secondaryBtn: {
      background: isDark ? "rgba(30,41,59,0.8)" : "rgba(241,245,249,0.9)",
      color: isDark ? "#cbd5e1" : "#475569",
      padding: "12px 24px",
      borderRadius: 10,
      border: isDark
        ? "1.5px solid rgba(255,255,255,0.15)"
        : "1.5px solid rgba(0,0,0,0.12)",
      cursor: "pointer",
      fontWeight: 700,
      fontSize: 14,
      transition: "all 300ms ease",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    formRow: {
      display: "flex",
      gap: 18,
      marginBottom: 18,
      flexWrap: "wrap",
    },
    formGroup: {
      flex: "1 1 calc(50% - 9px)",
      minWidth: 220,
      display: "flex",
      flexDirection: "column",
    },
    formLabel: {
      marginBottom: 10,
      fontSize: 12,
      fontWeight: 700,
      color: isDark ? "#94a3b8" : "#475569",
      textTransform: "uppercase",
      letterSpacing: "0.6px",
    },
    errorText: {
      color: "#ef4444",
      fontSize: 11,
      marginTop: 6,
      fontWeight: 600,
    },
    closeBtn: {
      position: "absolute",
      top: 14,
      right: language === "ar" ? "auto" : 14,
      left: language === "ar" ? 14 : "auto",
      width: 40,
      height: 40,
      borderRadius: 10,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background: isDark ? "rgba(13,18,28,0.15)" : "rgba(255,255,255,0.92)",
      border: isDark
        ? "1px solid rgba(255,255,255,0.06)"
        : "1px solid rgba(15,23,42,0.06)",
      cursor: "pointer",
      transition: "all 200ms ease",
      zIndex: 20,
      color: isDark ? "#94a3b8" : "#475569",
    },
  };

  const styles = { ...defaultStyles, ...(stylesProp || {}) };
  const resolvedFonts = { body: "14px", subtitle: "1rem", ...fonts };
  const resolvedSpacing = {
    margin: "1.5rem",
    gap: "1rem",
    padding: "1rem",
    ...spacing,
  };

  const infinityCode =
    selectedEmployee?.fullEmployeeData?.infinityCode ||
    selectedEmployee?.infinityCode ||
    selectedEmployee?.id ||
    null;

  // Seed the form from the row data already in memory; the full record is
  // fetched from the backend right after mount and replaces this state.
  const [formData, setFormData] = useState(() =>
    buildFormState({
      sr: selectedEmployee?.name,
      email: selectedEmployee?.email,
      phoneNumber: selectedEmployee?.phone,
      departmentId: selectedEmployee?.departmentId,
      position: selectedEmployee?.position,
      laborMode: selectedEmployee?.laborMode,
      clientName: selectedEmployee?.company || clientName,
      employeeState: selectedEmployee?.status || "Active",
      netSalary: selectedEmployee?.salary,
      arabicName: selectedEmployee?.arabicName,
      workingCompany: selectedEmployee?.workingCompany,
      ...(selectedEmployee?.fullEmployeeData || {}),
    })
  );
  const [formErrors, setFormErrors] = useState({});
  const [isLoadingFullRecord, setIsLoadingFullRecord] = useState(false);
  const [fullRecordError, setFullRecordError] = useState(null);
  // The form as loaded from the server, and which fields the server actually
  // returned. Saving is only possible once this is set, and only sends a
  // field the server returned or the user changed — a field the server
  // didn't send (e.g. salary/bank for non-admins) is never sent back empty.
  const [loadedFormData, setLoadedFormData] = useState(null);
  const [loadedFieldNames, setLoadedFieldNames] = useState(null);

  // Department options with id+name pairs fetched from the Departments API
  // (the `departments` prop only carries derived name strings).
  const [departmentOptions, setDepartmentOptions] = useState([]);
  // Text shown inside the department dropdown (the department NAME, while
  // formData.departmentId stores the selected department's id).
  const [departmentSearchTerm, setDepartmentSearchTerm] = useState(() => {
    const name = selectedEmployee?.department;
    return name && name !== "N/A" ? name : "";
  });

  // Working-company choices — all client companies for real admins,
  // visibility-scoped companies for promoted admin-view accounts.
  const { companyOptions, companiesLoading } = useWorkingCompanyOptions({
    companies,
    clientName,
  });
  // Text shown inside the working-company dropdown.
  const [workingCompanySearchTerm, setWorkingCompanySearchTerm] = useState(
    () =>
      selectedEmployee?.fullEmployeeData?.workingCompany ||
      selectedEmployee?.workingCompany ||
      ""
  );

  const departmentsWorkingCompany =
    formData.workingCompany ||
    selectedEmployee?.workingCompany ||
    selectedEmployee?.fullEmployeeData?.workingCompany ||
    "";

  const resolvedDepartmentsClientName =
    clientName ||
    selectedEmployee?.fullEmployeeData?.clientName ||
    localStorage.getItem("clientName") ||
    "";

  useEffect(() => {
    if (!resolvedDepartmentsClientName || !departmentsWorkingCompany) return;
    let cancelled = false;

    (async () => {
      try {
        const params = new URLSearchParams({
          clientName: resolvedDepartmentsClientName,
          workingCompany: departmentsWorkingCompany,
        });
        const response = await apiFetch(`${API_URL}/api/departments?${params}`);
        const json = await response.json().catch(() => null);
        if (!response.ok || json?.success === false) {
          throw new Error(json?.message || "Failed to load departments");
        }
        const list = Array.isArray(json?.data)
          ? json.data
          : Array.isArray(json)
          ? json
          : [];
        if (!cancelled) setDepartmentOptions(list);
      } catch (err) {
        console.error("Failed to fetch departments:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedDepartmentsClientName, departmentsWorkingCompany]);

  // Manager-picker dropdown — lightweight employee list for this tenant.
  const [managerOptions, setManagerOptions] = useState([]);
  const [managerSearchTerm, setManagerSearchTerm] = useState(() => {
    const code = selectedEmployee?.fullEmployeeData?.firstLineManagerClientCode || "";
    const name = selectedEmployee?.fullEmployeeData?.firstLineManagerName || "";
    return code ? `${code} — ${name}` : name;
  });
  useEffect(() => {
    const client =
      clientName ||
      selectedEmployee?.fullEmployeeData?.clientName ||
      localStorage.getItem("clientName") ||
      "";
    if (!client) return;
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({ clientName: client });
        const response = await apiFetch(`${API_URL}/employees/manager-options?${params}`);
        const json = await response.json().catch(() => null);
        if (!cancelled && response.ok && Array.isArray(json?.data)) {
          setManagerOptions(json.data);
        }
      } catch (err) {
        console.warn("Could not load manager options:", err);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientName]);

  const handleCreateDepartment = async (name) => {
    try {
      const response = await apiFetch(`${API_URL}/api/departments`, {
        method: "POST",
        body: JSON.stringify({
          clientName: resolvedDepartmentsClientName,
          workingCompany: departmentsWorkingCompany,
          name,
        }),
      });
      const json = await response.json().catch(() => null);
      if (!response.ok || json?.success === false) {
        throw new Error(json?.message || "Failed to create department");
      }
      const created = json?.data ?? json;
      setDepartmentOptions((prev) => [...prev, created]);
      return created;
    } catch (err) {
      console.error("Failed to create department:", err);
      window.alert(err.message || (language === "ar" ? "تعذر إنشاء القسم" : "Failed to create department"));
      return null;
    }
  };

  // Search text shown inside the select dropdowns, keyed by field name.
  const selectFields = ALL_FIELDS.filter((f) => f.type === "select");
  const labelForOption = (field, value) => {
    const option = field.options?.find((opt) => opt.key === value);
    return option ? option.label[language] || option.label.en : value || "";
  };
  const [selectSearchTerms, setSelectSearchTerms] = useState(() =>
    Object.fromEntries(
      selectFields.map((field) => [
        field.name,
        labelForOption(field, selectedEmployee?.fullEmployeeData?.[field.name]),
      ])
    )
  );

  // Fetch the COMPLETE employee record (all database fields) for editing.
  useEffect(() => {
    if (!infinityCode) return;
    let cancelled = false;

    const loadFullRecord = async () => {
      try {
        setIsLoadingFullRecord(true);
        setFullRecordError(null);
        const response = await apiFetch(
          `${API_URL}/employees/select/${infinityCode}`,
          { method: "GET" }
        );
        const json = await response.json().catch(() => null);
        const record = json?.employee || null;
        if (!response.ok || !record) {
          throw new Error(json?.message || "Failed to load employee record");
        }
        if (cancelled) return;
        const loaded = buildFormState(record);
        setFormData(loaded);
        setLoadedFormData(loaded);
        setLoadedFieldNames(new Set(Object.keys(record)));
        setDepartmentSearchTerm(record.department?.name ?? "");
        setWorkingCompanySearchTerm(record.workingCompany ?? "");
        // Sync manager search term from the freshly-loaded record.
        const mgrCode = record.firstLineManagerClientCode || "";
        const mgrName = record.firstLineManagerName || "";
        setManagerSearchTerm(mgrCode ? `${mgrCode} — ${mgrName}` : mgrName);
        setSelectSearchTerms(
          Object.fromEntries(
            selectFields.map((field) => [
              field.name,
              labelForOption(field, record[field.name]),
            ])
          )
        );
      } catch (err) {
        console.error("Failed to fetch full employee record:", err);
        if (!cancelled) setFullRecordError(err.message);
      } finally {
        if (!cancelled) setIsLoadingFullRecord(false);
      }
    };

    loadFullRecord();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infinityCode]);

  // ── Document upload state ──────────────────────────────────────────────────
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadDocType, setUploadDocType] = useState("contract");
  const fileUploadRef = useRef(null);

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    addFiles(selected);
    e.target.value = "";
  };

  const addFiles = (incoming) => {
    setUploadedFiles((prev) => {
      const existingNames = new Set(prev.map((f) => f.name));
      return [...prev, ...incoming.filter((f) => !existingNames.has(f.name))];
    });
  };

  const handleRemoveFile = (fileName) => {
    setUploadedFiles((prev) => prev.filter((f) => f.name !== fileName));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    addFiles(Array.from(e.dataTransfer.files || []));
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split(".").pop()?.toLowerCase();
    const colors = {
      pdf: "#ef4444",
      doc: "#2563eb",
      docx: "#2563eb",
      xls: "#16a34a",
      xlsx: "#16a34a",
      jpg: "#f59e0b",
      jpeg: "#f59e0b",
      png: "#8b5cf6",
    };
    return colors[ext] || "#64748b";
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  // ──────────────────────────────────────────────────────────────────────────

  const handleModalClick = (e) => {
    if (e.target === e.currentTarget) {
      setShowEditEmployee(false);
      setSelectedEmployee(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: "" });
    }
  };

  // Editing the national ID to a full valid Egyptian ID refills date of
  // birth, age, gender and ID region from it (the ID is the source of truth),
  // plus nationality only when empty. Opening the modal never overwrites
  // stored values — only an actual change to the ID does.
  const nationalIdInfo = parseEgyptianNationalId(formData.nationalId);
  const nationalIdInvalid =
    normalizeNationalId(formData.nationalId).length >= 14 && !nationalIdInfo;
  const handleNationalIdChange = (e) => {
    const value = e.target.value;
    const info = parseEgyptianNationalId(value);
    const isAr = language === "ar";
    setFormData((prev) => ({
      ...prev,
      nationalId: value,
      ...(info && {
        dob: info.dateOfBirth,
        age: String(info.age),
        gender: info.gender,
        idRegion: isAr ? info.governorate.ar : info.governorate.en,
        nationality: prev.nationality || (isAr ? "مصري" : "Egyptian"),
      }),
    }));
    if (info) {
      setSelectSearchTerms((prev) => ({
        ...prev,
        gender: info.gender === "Male" ? (isAr ? "ذكر" : "Male") : (isAr ? "أنثى" : "Female"),
      }));
    }
  };

  // Gross/Net salary are mutually derived: whichever one the user types into
  // becomes `salaryInputSource` and is sent to the backend as the
  // authoritative figure on save; the other field locks and shows a live
  // preview fetched from the server (same tax/insurance formulas the save
  // itself will use — never duplicated in the frontend).
  const salaryPreviewTimer = useRef(null);
  const handleSalaryFieldChange = (name, value) => {
    const source = name === "grossSalary" ? "gross" : "net";
    setFormData((prev) => ({ ...prev, [name]: value, salaryInputSource: source }));

    if (salaryPreviewTimer.current) clearTimeout(salaryPreviewTimer.current);
    if (value === "" || value === null || isNaN(Number(value))) return;

    salaryPreviewTimer.current = setTimeout(async () => {
      try {
        const response = await apiFetch(`${API_URL}/employees/salary-preview`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            grossSalary: source === "gross" ? value : null,
            netSalary: source === "net" ? value : null,
            salaryInputSource: source,
            insuranceSalary: formData.insuranceSalary || null,
          }),
        });
        const data = await response.json();
        if (data?.success) {
          setFormData((prev) => ({
            ...prev,
            grossSalary: source === "gross" ? prev.grossSalary : data.grossSalary ?? prev.grossSalary,
            netSalary: source === "net" ? prev.netSalary : data.netSalary ?? prev.netSalary,
          }));
        }
      } catch (err) {
        console.error("Salary preview failed:", err);
      }
    }, 400);
  };

  const validateForm = () => {
    const errors = {};
    if (!String(formData.sr || "").trim())
      errors.sr = text[language].nameRequired;
    // Email is no longer mandatory by itself — a login account can be tied
    // to either an email or a phone number, so only require that at least
    // one of the two is present. Whichever IS provided still gets
    // format-checked.
    const hasEmail = !!String(formData.email || "").trim();
    const hasPhone = !!String(formData.phoneNumber || "").trim();
    if (hasEmail && !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = text[language].emailInvalid;
    }
    if (!hasEmail && !hasPhone) {
      errors.email = text[language].emailOrPhoneRequired;
      errors.phoneNumber = text[language].emailOrPhoneRequired;
    }
    // Department is nice-to-have but must never block saving an otherwise
    // valid edit (e.g. Status) on an existing employee that predates having
    // a department assigned — only Name/Email(or Phone) genuinely block the update.
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Only after the server's record loaded: saving the table-row seed would
  // blank every field the row doesn't carry.
  const canSave = !isLoadingFullRecord && !fullRecordError && loadedFormData !== null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    if (!validateForm()) {
      alert(
        language === "ar"
          ? "من فضلك راجع الحقول المميزة بالأحمر قبل الحفظ (الاسم/الإيميل أو الهاتف)."
          : "Please review the fields highlighted in red before saving (Name/Email or Phone)."
      );
      return;
    }

    // Upload documents first if any
    const fileTargetCode = infinityCode;
    if (uploadedFiles.length > 0 && fileTargetCode) {
      for (const file of uploadedFiles) {
        try {
          await uploadEmployeeDocument(fileTargetCode, file, uploadDocType);
        } catch (err) {
          console.error("Upload failed for", file.name, err);
          alert(
            (language === "ar"
              ? "فشل رفع المستند: "
              : "Failed to upload document: ") + (err.message || file.name)
          );
        }
      }
      setUploadedFiles([]);
    }

    // Submit every field the server returned (with its current value) plus
    // any field the user changed. A field the server did NOT return — e.g.
    // salary/bank, which it strips for non-admins — is left out unless the
    // user typed into it, so the backend keeps its stored value instead of
    // overwriting it with the blank the form showed.
    // Document checklist flags are never sent from here: they aren't on this
    // form (the Documents button owns them), and re-sending the values loaded
    // when the form opened could undo a checklist save made meanwhile.
    // Same for the compensation fields when that section is hidden.
    const payload = Object.fromEntries(
      Object.entries(formData).filter(
        ([field, value]) =>
          !DOCUMENT_FIELD_NAMES.has(field) &&
          (canViewCompensation || !COMPENSATION_FIELD_NAMES.has(field)) &&
          (loadedFieldNames.has(field) ||
            String(value ?? "") !== String(loadedFormData[field] ?? ""))
      )
    );
    await handleUpdateEmployee(payload, []);
  };

  const handleFormKeyDown = (e) => {
    if (e.key !== "Enter") return;
    const target = e.target;
    if (!target) return;
    const tag = (target.tagName || "").toLowerCase();
    if (tag === "button" || target.type === "submit") return;
    if (target.closest && target.closest("[data-reusable-dropdown]")) return;
    e.preventDefault();
  };

  // Shared styles
  const enhancedLabel = {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    color: isDark ? "rgba(241, 245, 249, 0.9)" : "rgba(15, 23, 42, 0.8)",
    fontSize: resolvedFonts.body,
    fontWeight: "600",
    marginBottom: "0.625rem",
    letterSpacing: "0.01em",
  };

  const enhancedSectionTitle = {
    color: isDark ? "rgba(255, 255, 255, 0.95)" : "rgba(26, 26, 26, 0.9)",
    fontSize: resolvedFonts.subtitle,
    fontWeight: "700",
    margin: `${resolvedSpacing.margin} 0 ${screenSize === "xs" ? "1rem" : "1.25rem"} 0`,
    paddingBottom: "0.75rem",
    borderBottom: isDark
      ? "2px solid rgba(59, 130, 246, 0.3)"
      : "2px solid rgba(59, 130, 246, 0.2)",
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
  };

  const checkboxLabel = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 14px",
    borderRadius: 10,
    background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
    border: isDark
      ? "1px solid rgba(255,255,255,0.07)"
      : "1px solid rgba(0,0,0,0.07)",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
    color: isDark ? "#cbd5e1" : "#374151",
    userSelect: "none",
    flex: "1 1 calc(33% - 8px)",
    minWidth: 200,
  };

  // ── Field renderers ────────────────────────────────────────────────────────
  const renderBooleanField = (field) => (
    <label key={field.name} style={checkboxLabel}>
      <input
        type="checkbox"
        name={field.name}
        checked={Boolean(formData[field.name])}
        onChange={handleInputChange}
        style={{ width: 16, height: 16, accentColor: "#2e7cba" }}
      />
      {field.label[language] || field.label.en}
    </label>
  );

  const renderSelectField = (field) => (
    <div key={field.name} style={styles.formGroup}>
      <label style={enhancedLabel}>
        {field.label[language] || field.label.en}
        {field.required ? " *" : ""}
      </label>
      <div style={{ position: "relative" }}>
        <ReusableDropdown
          items={field.options}
          value={selectSearchTerms[field.name] ?? ""}
          onChange={(val) =>
            setSelectSearchTerms((prev) => ({ ...prev, [field.name]: val }))
          }
          onSelect={(item) => {
            setFormData((prev) => ({ ...prev, [field.name]: item.key }));
            setSelectSearchTerms((prev) => ({
              ...prev,
              [field.name]: item.label[language] || item.label.en,
            }));
          }}
          placeholder={field.label[language] || field.label.en}
          getItemKey={(it) => it.key}
          getItemLabel={(it) => it.label[language] || it.label.en}
          theme={theme}
          language={language}
          maxItems={10}
          inputStyle={unifiedControlStyle(theme, language === "ar")}
        />
      </div>
      {formErrors[field.name] && (
        <span style={styles.errorText}>{formErrors[field.name]}</span>
      )}
    </div>
  );

  const renderDepartmentField = (field) => (
    <div key={field.name} style={styles.formGroup}>
      <label style={enhancedLabel}>
        <Building size={14} />
        {field.label[language] || field.label.en}
        {field.required ? " *" : ""}
      </label>
      <div style={{ position: "relative" }}>
        <ReusableDropdown
          items={departmentsWorkingCompany ? departmentOptions : []}
          value={departmentSearchTerm}
          onChange={(val) => setDepartmentSearchTerm(val)}
          onSelect={(item) => {
            setFormData((prev) => ({ ...prev, [field.name]: item?.id ?? null }));
            setDepartmentSearchTerm(item?.name ?? "");
          }}
          placeholder={
            !departmentsWorkingCompany
              ? language === "ar"
                ? "اختر شركة العمل أولاً"
                : "Select working company first"
              : text[language].selectDepartment
          }
          disabled={!departmentsWorkingCompany}
          getItemKey={(it) =>
            typeof it === "string" ? it : it.id || String(it)
          }
          getItemLabel={(it) =>
            typeof it === "string" ? it : it.name || String(it)
          }
          theme={theme}
          language={language}
          maxItems={10}
          inputStyle={unifiedControlStyle(theme, language === "ar")}
          allowCreate={canAddDepartment}
          onCreate={handleCreateDepartment}
        />
      </div>
      {formErrors[field.name] && (
        <span style={styles.errorText}>{formErrors[field.name]}</span>
      )}
    </div>
  );

  const renderWorkingCompanyField = (field) => (
    <div key={field.name} style={styles.formGroup}>
      <label style={enhancedLabel}>
        <Building size={14} />
        {field.label[language] || field.label.en}
        {field.required ? " *" : ""}
      </label>
      <div style={{ position: "relative" }}>
        <ReusableDropdown
          items={companyOptions}
          value={workingCompanySearchTerm}
          onChange={(val) => setWorkingCompanySearchTerm(val)}
          onSelect={(item) => {
            const name = typeof item === "string" ? item : String(item);
            setFormData((prev) => {
              if (name === prev.workingCompany) return prev;
              // Departments depend on the selected company — reset them.
              return { ...prev, workingCompany: name, departmentId: "" };
            });
            if (name !== formData.workingCompany) setDepartmentSearchTerm("");
            setWorkingCompanySearchTerm(name);
          }}
          placeholder={
            companiesLoading
              ? language === "ar"
                ? "جاري التحميل..."
                : "Loading..."
              : language === "ar"
              ? "اختر شركة العمل"
              : "Select working company"
          }
          disabled={companiesLoading}
          getItemKey={(it) => String(it)}
          getItemLabel={(it) => String(it)}
          theme={theme}
          language={language}
          maxItems={10}
          inputStyle={unifiedControlStyle(theme, language === "ar")}
        />
      </div>
      {formErrors[field.name] && (
        <span style={styles.errorText}>{formErrors[field.name]}</span>
      )}
    </div>
  );

  const renderInputField = (field) => (
    <div key={field.name} style={styles.formGroup}>
      <label style={enhancedLabel}>
        {field.label[language] || field.label.en}
        {field.required ? " *" : ""}
        {field.readOnly && (
          <Lock
            size={12}
            style={{ opacity: 0.6 }}
            title={
              field.readOnlyHint
                ? field.readOnlyHint[language] || field.readOnlyHint.en
                : language === "ar"
                  ? "غير قابل للتعديل — يتغير فقط عن طريق مسؤول النظام"
                  : "Not editable — can only be changed by a superadmin"
            }
          />
        )}
      </label>
      {field.type === "date" && !field.readOnly ? (
        <DatePicker
          theme={theme}
          isRTL={language === "ar"}
          value={formData[field.name] || ""}
          onChange={(v) => handleInputChange({ target: { name: field.name, value: v } })}
        />
      ) : (
      <input
        type={field.type === "number" ? "number" : field.type}
        name={field.name}
        value={formData[field.name] ?? ""}
        onChange={field.name === "nationalId" ? handleNationalIdChange : handleInputChange}
        inputMode={field.name === "nationalId" ? "numeric" : undefined}
        readOnly={field.readOnly}
        disabled={field.readOnly}
        style={{
          ...unifiedControlStyle(theme, language === "ar"),
          ...(field.readOnly
            ? { opacity: 0.65, cursor: "not-allowed" }
            : {}),
          ...(field.name === "nationalId" && nationalIdInvalid ? { borderColor: "#ef4444" } : {}),
        }}
        dir={field.dir}
        step={field.type === "number" ? "any" : undefined}
      />
      )}
      {field.name === "nationalId" && nationalIdInvalid && (
        <span style={{ fontSize: 12, marginTop: 4, color: "#ef4444" }}>
          {language === "ar"
            ? "الرقم القومي غير صحيح — لم يتم ملء البيانات تلقائيًا."
            : "Invalid national ID — details were not auto-filled."}
        </span>
      )}
      {formErrors[field.name] && (
        <span style={styles.errorText}>{formErrors[field.name]}</span>
      )}
    </div>
  );

  // Gross + Net render together as one linked pair: typing in one locks the
  // other (read-only, live-calculated) instead of the plain single-input
  // treatment the rest of the compensation section gets.
  const renderSalaryPairField = () => {
    const grossField = ALL_FIELDS.find((f) => f.name === "grossSalary");
    const netField = ALL_FIELDS.find((f) => f.name === "netSalary");
    const renderOne = (field, name) => (
      <div key={name} style={styles.formGroup}>
        <label style={enhancedLabel}>{field.label[language] || field.label.en}</label>
        <input
          type="number"
          name={name}
          value={formData[name] ?? ""}
          onChange={(e) => handleSalaryFieldChange(name, e.target.value)}
          style={unifiedControlStyle(theme, language === "ar")}
          step="any"
        />
        {formErrors[name] && <span style={styles.errorText}>{formErrors[name]}</span>}
      </div>
    );

    return (
      <React.Fragment key="salary-pair">
        {renderOne(grossField, "grossSalary")}
        {renderOne(netField, "netSalary")}
      </React.Fragment>
    );
  };

  const renderManagerDropdownField = (field) => (
    <div key={field.name} style={styles.formGroup}>
      <label style={enhancedLabel}>
        {field.label[language] || field.label.en}
      </label>
      <div style={{ position: "relative" }}>
        <ReusableDropdown
          items={managerOptions}
          value={managerSearchTerm}
          onChange={(val) => setManagerSearchTerm(val)}
          onSelect={(item) => {
            const code = item?.clientCode ?? "";
            const name = item?.sr ?? "";
            setFormData((prev) => ({
              ...prev,
              firstLineManagerClientCode: code,
              firstLineManagerName: name,
            }));
            setManagerSearchTerm(code ? `${code} — ${name}` : name);
          }}
          placeholder={
            language === "ar"
              ? "ابحث بالاسم أو الكود..."
              : "Search manager by name or code..."
          }
          getItemKey={(it) => it.infinityCode || it.clientCode || it.sr}
          getItemLabel={(it) => {
            const code = it.clientCode ? `${it.clientCode} — ` : "";
            return language === "ar"
              ? `${code}${it.arabicName || it.sr || ""}`
              : `${code}${it.sr || it.arabicName || ""}`;
          }}
          theme={theme}
          language={language}
          maxItems={15}
          inputStyle={unifiedControlStyle(theme, language === "ar")}
        />
      </div>
      {formErrors[field.name] && (
        <span style={styles.errorText}>{formErrors[field.name]}</span>
      )}
    </div>
  );

  const renderField = (field) => {
    if (field.name === "grossSalary") return null; // rendered together with netSalary below
    if (field.name === "netSalary") return renderSalaryPairField();
    switch (field.type) {
      case "boolean":
        return renderBooleanField(field);
      case "select":
        return renderSelectField(field);
      case "department":
        return renderDepartmentField(field);
      case "workingCompany":
        return renderWorkingCompanyField(field);
      case "managerDropdown":
        return renderManagerDropdownField(field);
      default:
        return renderInputField(field);
    }
  };

  // Guard after all hooks
  if (!selectedEmployee) return null;

  return (
    <div style={styles.modal} onClick={handleModalClick}>
      <div style={styles.modalContent} className="modal-content">

        {/* ── Modal Header ── */}
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>
            <Edit
              size={screenSize === "xs" ? 18 : 20}
              style={{
                marginRight: language === "ar" ? 0 : "0.5rem",
                marginLeft: language === "ar" ? "0.5rem" : 0,
              }}
            />
            {text[language]?.editEmployee || "Edit Employee"} -{" "}
            {selectedEmployee?.name}
          </h2>
          <button
            style={styles.closeBtn}
            onClick={() => {
              setShowEditEmployee(false);
              setSelectedEmployee(null);
            }}
            title={language === "ar" ? "إغلاق" : "Close"}
          >
            <X size={18} />
          </button>
        </div>

        {/* Full-record loading / error banner */}
        {isLoadingFullRecord && (
          <div
            style={{
              marginBottom: 16,
              padding: "10px 14px",
              borderRadius: 10,
              background: isDark
                ? "rgba(46,124,186,0.12)"
                : "rgba(46,124,186,0.08)",
              color: "#2e7cba",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {language === "ar"
              ? "جاري تحميل كامل بيانات الموظف..."
              : "Loading the complete employee record..."}
          </div>
        )}
        {fullRecordError && !isLoadingFullRecord && (
          <div
            style={{
              marginBottom: 16,
              padding: "10px 14px",
              borderRadius: 10,
              background: isDark
                ? "rgba(239,68,68,0.12)"
                : "rgba(239,68,68,0.08)",
              color: "#ef4444",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {language === "ar"
              ? "تعذر تحميل كامل البيانات — الحفظ معطل حتى لا تُمسح البيانات غير المحملة"
              : "Could not load the full record — saving is disabled so the fields that didn't load aren't erased"}
          </div>
        )}

        {/* ── Form ── */}
        <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown}>

          {formSections.map((section) => {
            const SectionIcon = section.icon;
            const booleanFields = section.fields.filter(
              (f) => f.type === "boolean"
            );
            const regularFields = section.fields.filter(
              (f) => f.type !== "boolean"
            );
            return (
              <div key={section.key}>
                <div style={enhancedSectionTitle}>
                  <SectionIcon size={18} />
                  {section.title[language] || section.title.en}
                </div>
                {regularFields.length > 0 && (
                  <div style={styles.formRow}>
                    {regularFields.map(renderField)}
                  </div>
                )}
                {booleanFields.length > 0 && (
                  <div style={{ ...styles.formRow, gap: 8 }}>
                    {booleanFields.map(renderField)}
                  </div>
                )}
              </div>
            );
          })}

          {/* ── Documents Upload Section ── */}
          <div style={enhancedSectionTitle}>
            <FileText size={18} />
            {language === "ar" ? "رفع المستندات" : "Upload Documents"}
          </div>

          {/* Document type selector */}
          <div style={{ marginBottom: 16 }}>
            <label style={enhancedLabel}>
              {language === "ar" ? "نوع المستند" : "Document Type"}
            </label>
            <ReusableDropdown
              items={[
                { value: "contract",    label: language === "ar" ? "عقد" : "Contract" },
                { value: "payslip",     label: language === "ar" ? "كشف راتب" : "Payslip" },
                { value: "certificate", label: language === "ar" ? "شهادة" : "Certificate" },
                { value: "policy",      label: language === "ar" ? "سياسة" : "Policy" },
              ]}
              value={[
                { value: "contract",    label: language === "ar" ? "عقد" : "Contract" },
                { value: "payslip",     label: language === "ar" ? "كشف راتب" : "Payslip" },
                { value: "certificate", label: language === "ar" ? "شهادة" : "Certificate" },
                { value: "policy",      label: language === "ar" ? "سياسة" : "Policy" },
              ].find((i) => i.value === uploadDocType)?.label || ""}
              onSelect={(item) => setUploadDocType(item.value)}
              onChange={() => {}}
              placeholder={language === "ar" ? "اختر نوع المستند" : "Select document type"}
              theme={theme}
              language={language}
              getItemLabel={(item) => item.label}
              getItemKey={(item) => item.value}
            />
          </div>

          {/* Hidden file input */}
          <input
            ref={fileUploadRef}
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.docx,.doc,.xlsx,.xls"
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />

          {/* Drop zone */}
          <div
            onClick={() => fileUploadRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDrop}
            style={{
              border: `2px dashed ${
                isDragOver
                  ? "#2e7cba"
                  : isDark
                  ? "rgba(255,255,255,0.15)"
                  : "rgba(15,23,42,0.15)"
              }`,
              borderRadius: 12,
              padding: "24px 20px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 10,
              cursor: "pointer",
              transition: "border-color 200ms, background 200ms",
              background: isDragOver
                ? isDark
                  ? "rgba(46,124,186,0.08)"
                  : "rgba(46,124,186,0.04)"
                : "transparent",
              marginBottom: uploadedFiles.length > 0 ? 14 : 0,
              userSelect: "none",
            }}
          >
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 10,
                background: isDark
                  ? "rgba(46,124,186,0.15)"
                  : "rgba(46,124,186,0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Upload
                size={20}
                style={{ color: "#2e7cba" }}
              />
            </div>
            <div style={{ textAlign: "center" }}>
              <p
                style={{
                  margin: 0,
                  fontSize: 14,
                  fontWeight: 600,
                  color: isDark ? "#cbd5e1" : "#374151",
                }}
              >
                {language === "ar"
                  ? "اضغط لاختيار ملفات أو اسحبها هنا"
                  : "Click to select files or drag & drop"}
              </p>
              <p
                style={{
                  margin: "4px 0 0",
                  fontSize: 12,
                  color: isDark ? "#64748b" : "#94a3b8",
                }}
              >
                {language === "ar"
                  ? "PDF، JPG، PNG، DOCX، XLSX"
                  : "PDF, JPG, PNG, DOCX, XLSX"}
              </p>
            </div>
          </div>

          {/* Attached file list */}
          {uploadedFiles.length > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                marginBottom: 4,
              }}
            >
              {/* Counter badge */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 4,
                }}
              >
                <FileText
                  size={13}
                  style={{ color: isDark ? "#94a3b8" : "#64748b" }}
                />
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: isDark ? "#94a3b8" : "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {uploadedFiles.length}{" "}
                  {language === "ar"
                    ? uploadedFiles.length === 1
                      ? "ملف مرفق"
                      : "ملفات مرفقة"
                    : uploadedFiles.length === 1
                    ? "file attached"
                    : "files attached"}
                </span>
              </div>

              {uploadedFiles.map((file) => (
                <div
                  key={file.name}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 14px",
                    borderRadius: 10,
                    background: isDark
                      ? "rgba(255,255,255,0.04)"
                      : "rgba(0,0,0,0.03)",
                    border: isDark
                      ? "1px solid rgba(255,255,255,0.07)"
                      : "1px solid rgba(0,0,0,0.07)",
                    transition: "background 150ms",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      minWidth: 0,
                    }}
                  >
                    {/* File type dot */}
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 7,
                        background: `${getFileIcon(file.name)}18`,
                        border: `1px solid ${getFileIcon(file.name)}33`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <FileText
                        size={14}
                        style={{ color: getFileIcon(file.name) }}
                      />
                    </div>

                    <div style={{ minWidth: 0 }}>
                      <p
                        style={{
                          margin: 0,
                          fontSize: 13,
                          fontWeight: 600,
                          color: isDark ? "#e2e8f0" : "#1e293b",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "38ch",
                        }}
                      >
                        {file.name}
                      </p>
                      <p
                        style={{
                          margin: "2px 0 0",
                          fontSize: 11,
                          color: isDark ? "#64748b" : "#94a3b8",
                        }}
                      >
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleRemoveFile(file.name)}
                    title={language === "ar" ? "إزالة" : "Remove"}
                    style={{
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: 6,
                      borderRadius: 6,
                      color: "#ef4444",
                      display: "flex",
                      alignItems: "center",
                      flexShrink: 0,
                      transition: "background 150ms",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "rgba(239,68,68,0.1)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* ── Modal Actions ── */}
          <div style={styles.modalActions}>
            <button
              type="submit"
              style={{
                ...styles.primaryBtn,
                opacity: loading || !canSave ? 0.7 : 1,
                cursor:
                  loading || !canSave ? "not-allowed" : "pointer",
              }}
              disabled={loading || !canSave}
              onMouseEnter={handlePrimaryBtnMouseEnter}
              onMouseLeave={handlePrimaryBtnMouseLeave}
            >
              <Save size={screenSize === "xs" ? 14 : 16} />
              {loading ? text[language].updating : text[language].update}
              {!loading && uploadedFiles.length > 0 && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.25)",
                    fontSize: 10,
                    fontWeight: 800,
                    marginLeft: 2,
                  }}
                >
                  +{uploadedFiles.length}
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEmployeeModal;
